// pms-adapters / supabase / vegaMirror — the read side of the Vega mirror (VM1).
//
// `docs/VEGA-MIRROR-BRIEF.md` §9, migration 0142.
//
// ── Why this one is allowed to be Vega-shaped, and `stayFeed` is not ───────
//
// `stayFeed`'s whole design is that the board never learns which system it is
// reading — the adapter is disposable and the domain types outlive it. This
// adapter is the opposite on purpose. §4 of the brief settles it: a mirror is
// EVIDENCE, in the shape the evidence arrived in, and abstracting it would
// destroy its only purpose, because a mirror you cannot check against Vega's
// own screen is not a mirror.
//
// The boundary that keeps that safe is not here. `vega_*` is read by exactly
// two things — this surface, and `seed_from_vega()` (VM2), which is where the
// anti-corruption layer lives, materialised as a function rather than a class.
//
// ── One RPC, and why (ADR-0022 D2/D4, migration 20261001020100) ────────────
//
// Until 2026-10-01 these were plain PostgREST selects, on the argument that
// nothing here aggregates. That held until the tables outgrew PostgREST's
// `max_rows = 1000`, past which a select returns its first 1 000 rows with NO
// error. Every read is newest first, so the rows lost were the OLDEST in the
// window — and for the FACT AUTO plan, read whole on purpose, exactly the tail
// the tab exists to show. Measured on production that night: « Règlements »
// over 30 days held 1 053 rows (1 733 at the peak of 2025); the plan held
// 1 014 on 2026-09-13.
//
// So every read goes through `vega_mirror_read`, which returns ONE jsonb
// array (one row: the cap does not apply) and RAISES above 5 000 rows instead
// of truncating. The filters and the order moved into it verbatim, and the
// rows it returns carry the same snake_case columns, so the mappers below are
// unchanged.
//
// The gate is still where migration 0142 put it. The function is SECURITY
// INVOKER: RLS on `pms.mirror.read` decides every row, and a function running
// as its caller can raise but never widen. What it adds is the raise — a
// missing capability is 42501 here, where the table read answered with an
// empty list that said « Vega holds nothing ».

import {
  err,
  ok,
  type IsoDate,
  type MirrorCancellation,
  type MirrorFolioLine,
  type MirrorInvoice,
  type MirrorPayment,
  type MirrorReservation,
  type PropertyCode,
  type Result,
  type VegaMirror,
} from '@lemontaigu/pms-core';
import type { PostgrestError } from '@supabase/supabase-js';
import { toPmsError, type SupabaseClient } from './support.js';

/** One `vega_reservation` row, verbatim. */
interface ReservationRow {
  resa: string;
  guest_ref: string | null;
  booker_name: string | null;
  arrival_date: string | null;
  departure_date: string | null;
  status: string | null;
  party_kind: string | null;
  board: string | null;
  source_ref: string | null;
  source_name: string | null;
  external_ref: string | null;
  booked_direct: boolean | null;
  channel_rooms: number | null;
  channel_amount: number | null;
  created_on: string | null;
  modified_on: string | null;
}

/** One `vega_cancellation` row, verbatim. */
interface CancellationRow {
  resa: string | null;
  guest_ref: string | null;
  cancelled_on: string | null;
  arrival_date: string | null;
  departure_date: string | null;
  room: string | null;
  reason: string | null;
  rooms: number | null;
  adults: number | null;
  ca_perdu: number | null;
  outlet: string | null;
}

/** One `vega_payment` row, verbatim. */
interface PaymentRow {
  kind: string | null;
  kind_label: string | null;
  reference: string | null;
  movement_date: string | null;
  name: string | null;
  method: string | null;
  client_ref: string | null;
  label: string | null;
  invoice_total: number | null;
  settled_amount: number | null;
}

/** One `vega_invoice` row, verbatim. */
interface InvoiceRow {
  facture: string;
  invoice_date: string | null;
  name: string | null;
  client_ref: string | null;
  deposit_applied: number | null;
  settled_amount: number | null;
  invoice_total: number | null;
  subtotal_10pct: number | null;
  subtotal_20pct: number | null;
  subtotal_exempt: number | null;
  subtotal_5_5pct: number | null;
}

/** One `vega_folio_line` row, verbatim. */
interface FolioLineRow {
  resa: string | null;
  guest_ref: string | null;
  label: string | null;
  product_code: string | null;
  qty: number | null;
  unit_price: number | null;
  total: number | null;
  line_date: string | null;
  end_date: string | null;
  posted_through: string | null;
}

// The column lists live in `vega_mirror_read_query` now, one per branch; its
// migration probe pins each list against these row types.

const toReservation = (r: ReservationRow): MirrorReservation => ({
  resa: r.resa,
  guestRef: r.guest_ref,
  bookerName: r.booker_name,
  arrivalDate: r.arrival_date as IsoDate | null,
  departureDate: r.departure_date as IsoDate | null,
  status: r.status as MirrorReservation['status'],
  partyKind: r.party_kind as MirrorReservation['partyKind'],
  board: r.board as MirrorReservation['board'],
  sourceRef: r.source_ref,
  sourceName: r.source_name,
  externalRef: r.external_ref,
  bookedDirect: r.booked_direct,
  channelRooms: r.channel_rooms,
  channelAmount: r.channel_amount,
  createdOn: r.created_on as IsoDate | null,
  modifiedOn: r.modified_on as IsoDate | null,
});

const toCancellation = (r: CancellationRow): MirrorCancellation => ({
  resa: r.resa,
  guestRef: r.guest_ref,
  cancelledOn: r.cancelled_on as IsoDate | null,
  arrivalDate: r.arrival_date as IsoDate | null,
  departureDate: r.departure_date as IsoDate | null,
  room: r.room,
  reason: r.reason,
  rooms: r.rooms,
  adults: r.adults,
  caPerdu: r.ca_perdu,
  outlet: r.outlet as MirrorCancellation['outlet'],
});

const toPayment = (r: PaymentRow): MirrorPayment => ({
  kind: r.kind as MirrorPayment['kind'],
  kindLabel: r.kind_label,
  reference: r.reference,
  movementDate: r.movement_date as IsoDate | null,
  name: r.name,
  method: r.method,
  clientRef: r.client_ref,
  label: r.label,
  // Passed through EXACTLY as stored: signed, and null where a deposit settled
  // the movement. Coalescing either one to 0 would turn two different facts —
  // « a credit of 219.96 € » and « nothing moved here » — into the same figure.
  //
  // ⚠ The two are NOT interchangeable: `invoice_total` is the facture's total
  // repeated on every settlement line, `settled_amount` is this movement's own
  // value. A total over the first double-counts every part-deposit facture.
  invoiceTotal: r.invoice_total,
  settledAmount: r.settled_amount,
});

const toInvoice = (r: InvoiceRow): MirrorInvoice => ({
  facture: r.facture,
  invoiceDate: r.invoice_date as IsoDate | null,
  name: r.name,
  clientRef: r.client_ref,
  // All fields pass through untouched. `0.00` and `null` are different facts
  // here — a facture that came to nothing, and one we could not read — and a
  // defensive `?? 0` would erase the distinction 0169/0171 exist to preserve.
  depositApplied: r.deposit_applied,
  settledAmount: r.settled_amount,
  invoiceTotal: r.invoice_total,
  subtotal10Pct: r.subtotal_10pct,
  subtotal20Pct: r.subtotal_20pct,
  subtotalExempt: r.subtotal_exempt,
  subtotal5_5Pct: r.subtotal_5_5pct,
});

const toFolioLine = (r: FolioLineRow): MirrorFolioLine => ({
  resa: r.resa,
  guestRef: r.guest_ref,
  label: r.label,
  productCode: r.product_code,
  qty: r.qty,
  unitPrice: r.unit_price,
  total: r.total,
  lineDate: r.line_date as IsoDate | null,
  endDate: r.end_date as IsoDate | null,
  postedThrough: r.posted_through as IsoDate | null,
});

/** A reversed range is refused rather than silently returning nothing.
 *
 *  An empty list from this surface means "the source holds nothing here", and
 *  that is a statement a reader will act on. It must never also mean "the
 *  caller passed the dates the wrong way round" — the same reasoning
 *  `stayFeed` applies to its own range. The RPC refuses it too; checking here
 *  as well saves the round trip. */
const badRange = (from: IsoDate, to: IsoDate) => from > to;

type MirrorKind = 'reservations' | 'cancellations' | 'payments' | 'invoices' | 'folio';

/** The RPC's refusals, in the operator's words without the function's name in
 *  front. A missing capability is a DENIAL — never « Vega holds nothing ». */
function readError(e: PostgrestError): Result<never> {
  if (e.code === '42501') return err('forbidden', 'Accès réservé — Miroir Vega', { cause: e });
  if (e.code === 'P0001') {
    return err('validation', e.message.replace(/^vega_mirror_read:\s*/, ''), { cause: e });
  }
  return { ok: false, error: toPmsError(e) };
}

export class SupabaseVegaMirror implements VegaMirror {
  constructor(private readonly db: SupabaseClient) {}

  /** One bounded read — see the header. The filters, the order and the column
   *  list per kind are in `vega_mirror_read_query`; this side only maps. */
  private async read<Row, T>(
    kind: MirrorKind,
    propertyCode: PropertyCode,
    range: { from: IsoDate; to: IsoDate } | null,
    map: (row: Row) => T,
  ): Promise<Result<T[]>> {
    const r = await this.db.rpc('vega_mirror_read', {
      p_kind: kind,
      p_property_code: propertyCode,
      ...(range ? { p_from: range.from, p_to: range.to } : {}),
    });
    if (r.error) return readError(r.error);
    // A reply we cannot read is an error, not an empty mirror.
    if (!Array.isArray(r.data)) {
      return err('unknown', 'Miroir Vega : réponse illisible', { retryable: false });
    }
    return ok((r.data as Row[]).map(map));
  }

  async reservations(
    propertyCode: PropertyCode,
    from: IsoDate,
    to: IsoDate,
  ): Promise<Result<MirrorReservation[]>> {
    if (badRange(from, to)) {
      return err('validation', `Plage de dates inversée (${from} → ${to}).`);
    }
    // Overlap, not containment (in the RPC): a booking that spans the window
    // belongs in it. Filtering on `arrival_date` alone would hide exactly the
    // long stays and groups a mirror is most often opened to look at.
    return this.read<ReservationRow, MirrorReservation>(
      'reservations', propertyCode, { from, to }, toReservation);
  }

  async cancellations(
    propertyCode: PropertyCode,
    from: IsoDate,
    to: IsoDate,
  ): Promise<Result<MirrorCancellation[]>> {
    if (badRange(from, to)) {
      return err('validation', `Plage de dates inversée (${from} → ${to}).`);
    }
    // Filtered on WHEN THEY WERE CANCELLED, not on the stay they cancelled.
    // « what did we lose this month » is the question this table answers; the
    // arrival date is carried for context only.
    return this.read<CancellationRow, MirrorCancellation>(
      'cancellations', propertyCode, { from, to }, toCancellation);
  }

  async payments(
    propertyCode: PropertyCode,
    from: IsoDate,
    to: IsoDate,
  ): Promise<Result<MirrorPayment[]>> {
    if (badRange(from, to)) {
      return err('validation', `Plage de dates inversée (${from} → ${to}).`);
    }
    // A journal: filtered on WHEN THE MONEY MOVED, which is the only date these
    // rows carry. Ordered by date then reference so a facture's movements —
    // a deposit deducted and the balance settled — sit together.
    return this.read<PaymentRow, MirrorPayment>(
      'payments', propertyCode, { from, to }, toPayment);
  }

  async invoices(
    propertyCode: PropertyCode,
    from: IsoDate,
    to: IsoDate,
  ): Promise<Result<MirrorInvoice[]>> {
    if (badRange(from, to)) {
      return err('validation', `Plage de dates inversée (${from} → ${to}).`);
    }
    // A sales journal: filtered on the invoice date, which is the only date
    // these rows carry. Ordered by date then facture, so a day reads in the
    // order Vega numbered it.
    return this.read<InvoiceRow, MirrorInvoice>(
      'invoices', propertyCode, { from, to }, toInvoice);
  }

  async folioLines(propertyCode: PropertyCode): Promise<Result<MirrorFolioLine[]>> {
    // No range, and that is the design — see the port. The plan's whole
    // interest is its eleven-year tail, so a window would hide the rows worth
    // looking at and make « nothing here » mean « nothing lately ». Whole is
    // not unbounded: past 5 000 lines the RPC refuses rather than drop the tail.
    return this.read<FolioLineRow, MirrorFolioLine>('folio', propertyCode, null, toFolioLine);
  }
}
